export const normalizeMetaValues = (metaValues) => {
  if (Array.isArray(metaValues)) {
    return [...new Set(metaValues.filter(Boolean))];
  }

  if (typeof metaValues === "string") {
    return [
      ...new Set(
        metaValues
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
      ),
    ];
  }

  return [];
};

export const normalizeInboxThread = (thread) => ({
  ...thread,
  meta_values: normalizeMetaValues(thread?.meta_values),
});

export const hasThreadMeta = (thread, token) =>
  normalizeMetaValues(thread?.meta_values).includes(token);

export const setThreadMetaValue = (thread, token, enabled) => {
  const metaValues = normalizeMetaValues(thread?.meta_values);
  const nextMetaValues = enabled
    ? [...new Set([...metaValues, token])]
    : metaValues.filter((value) => value !== token);

  return { ...thread, meta_values: nextMetaValues };
};

export const threadMatchesFilter = (thread, filter = "") => {
  const metaValues = normalizeMetaValues(thread?.meta_values);

  if (filter === "all") return true;
  if (filter === "archived") return metaValues.includes("archived");
  if (filter === "unread") {
    return metaValues.includes("unread") && !metaValues.includes("archived");
  }
  if (filter === "priority") {
    return metaValues.includes("priority") && !metaValues.includes("archived");
  }

  return !metaValues.includes("archived");
};

export const mergeInboxPage = (
  current,
  page,
  { replace = false, position = "append" } = {},
) => {
  const normalizedPage = (page || []).map(normalizeInboxThread);
  if (replace || !Array.isArray(current)) return normalizedPage;

  const incomingIds = new Set(
    normalizedPage.map((thread) => String(thread.id)),
  );
  const retained = current.filter(
    (thread) => !incomingIds.has(String(thread.id)),
  );
  if (position === "front") return [...normalizedPage, ...retained];

  const incomingById = new Map(
    normalizedPage.map((thread) => [String(thread.id), thread]),
  );
  const updatedCurrent = current.map(
    (thread) => incomingById.get(String(thread.id)) || thread,
  );
  const currentIds = new Set(current.map((thread) => String(thread.id)));
  return [
    ...updatedCurrent,
    ...normalizedPage.filter((thread) => !currentIds.has(String(thread.id))),
  ];
};

export const updateThreadMetaInList = (
  threads,
  threadId,
  token,
  enabled,
  filter,
  { preserveMembership = false } = {},
) => {
  let found = false;
  let removed = false;

  const sourceThreads = Array.isArray(threads) ? threads : [];
  const nextThreads = sourceThreads.reduce((result, thread) => {
    if (String(thread.id) !== String(threadId)) {
      result.push(thread);
      return result;
    }

    found = true;
    const updated = setThreadMetaValue(thread, token, enabled);
    if (!preserveMembership && !threadMatchesFilter(updated, filter)) {
      removed = true;
      return result;
    }

    result.push(updated);
    return result;
  }, []);

  return { threads: nextThreads, found, removed };
};

export const extractThreadMetaTokens = (thread) => {
  if (Array.isArray(thread?.meta?.tokens)) return thread.meta.tokens;
  if (Array.isArray(thread?.meta)) {
    return thread.meta.map((item) => item?.token).filter(Boolean);
  }
  return normalizeMetaValues(thread?.meta_values);
};
